import { useEffect, useMemo, useRef, useState } from 'react';

import {
  Alert,
  AlertDescription,
  AlertIcon,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Heading,
  HStack,
  Icon,
  Link,
  Skeleton,
  Stack,
  Tab,
  TabList,
  Tabs,
  Text,
  useColorModeValue,
} from '@ecoinvent/ui';
import mixpanel from 'mixpanel-browser';
import { BiCalendar } from 'react-icons/bi';
import { IoEarth } from 'react-icons/io5';
import { useAuth } from 'react-oidc-context';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router';
import { localStorageKeys } from 'storageKeys';

import {
  CompareDataset,
  datasetToCompareItem,
  isSameDatasetRef,
  MAX_COMPARE_DATASETS,
  useCompare,
  useIsDatasetComparisonEnabled,
  v4DatasetToCompareItem,
} from 'components/shared/Compare';
import PageHead from 'components/shared/PageHead';
import useDatasetDocumentation from 'hooks/data/datasets/documentation/useDatasetDocumentation';
import { Dataset, v4Dataset } from 'hooks/data/datasets/types';
import useDataset from 'hooks/data/datasets/useDataset';
import useV4DatasetDocumentation from 'hooks/data/datasets/V4/documentation/useV4DatasetDocumentation';
import useV4Dataset from 'hooks/data/datasets/V4/useV4Dataset';
import { AccessResourceKey } from 'hooks/data/files/types';
import useReleaseVersions from 'hooks/data/versions/useReleaseVersions';
import useAccess from 'hooks/useAccess';
import { useIsShowingV4 } from 'hooks/useV4';
import useVersionInfo from 'hooks/useVersionInfo';
import RelatedDatasets from 'pages/Dataset/components/RelatedDatasets';
import ImpactAssessment from 'pages/ImpactAssessment';
import UpgradeNowUpsellDataset from 'pages/Search/components/UpgradeNowUpsellDataset';
import { checkAccessToResource } from 'utilities/auth/access';
import { MIXPANEL_EVENTS } from 'utilities/constants/mixpanel';
import { getSearchPath } from 'utilities/search/searchOrigin';

import DatasetNotFound from './components/DatasetNotFound';
import DatasetTabs from './components/DatasetTabs';
import PrivateDatasetView from './PrivateDatasetView';
import PublicDatasetView from './PublicDatasetView';

const loadViewMode = (urlPath: string): 'table' | 'graph' => {
  if (urlPath === 'supply-chain') return 'graph';
  const stored = localStorage.getItem(localStorageKeys.SUPPLY_CHAIN_VIEW_MODE);
  return stored === 'graph' ? 'graph' : 'table';
};

const saveViewMode = (mode: 'table' | 'graph') => {
  localStorage.setItem(localStorageKeys.SUPPLY_CHAIN_VIEW_MODE, mode);
};

const deriveSupplyChainMode = (urlPath: string): 'flows' | 'impact_assessment' =>
  urlPath === 'impact_assessment' ? 'impact_assessment' : 'flows';

const DatasetView = () => {
  const { pathname } = useLocation();
  const { spold_id } = useParams();
  const navigate = useNavigate();
  const urlPath = pathname.split('/')[5];
  const [viewMode, setViewMode] = useState<'table' | 'graph'>(() => loadViewMode(urlPath));
  const [supplyChainMode, setSupplyChainMode] = useState<'flows' | 'impact_assessment'>(() => deriveSupplyChainMode(urlPath));
  // Tracks the table tab supplyChainMode was last derived from, so toggling back to Supply chain
  // without changing table tabs preserves whatever sub-tab was picked inside Supply chain view.
  const lastDerivedTablePathRef = useRef(urlPath);

  const { isAuthenticated } = useAuth();
  const { ver, system_model } = useVersionInfo();
  const isShowingV4 = useIsShowingV4();
  // viewMode persists across V3/V4 in localStorage, but the graph view only exists on V4.
  const effectiveViewMode = isShowingV4 ? viewMode : 'table';

  const { data, isLoading, isError } = useDataset(spold_id, isShowingV4);
  const { data: V4Data, isLoading: isV4Loading, isError: isV4Error } = useV4Dataset(spold_id);
  const { data: documentationData, isLoading: isDocumentationLoading } = useDatasetDocumentation(spold_id);
  const { data: documentationV4Data, isLoading: isV4DocumentationLoading } = useV4DatasetDocumentation(spold_id);
  const { data: releaseVersions } = useReleaseVersions();
  const { data: accessData } = useAccess();
  const indicatorColor = useColorModeValue('gray.700', 'gray.400');
  const { addDataset, datasets: comparedDatasets } = useCompare();
  const isCompareEnabled = useIsDatasetComparisonEnabled();
  const switchBg = useColorModeValue('#E7E7E7', 'gray.700');
  const switchActiveBg = useColorModeValue('white', 'gray.600');
  const switchTextColor = useColorModeValue('gray.800', 'whiteAlpha.900');

  const hasALicence = Object.keys(releaseVersions ?? {}).some((key) =>
    checkAccessToResource(accessData, AccessResourceKey.SCORES, key)
  );

  // Where the breadcrumb goes: back to the search this dataset was opened from, or — for datasets
  // reached from the landing page or a shared link — a search for the dataset's own name.
  const datasetName = isShowingV4 ? V4Data?.name : data?.activity_name;
  const searchPath = useMemo(
    () => getSearchPath({ ver, system_model, spold_id, datasetName }),
    [ver, system_model, spold_id, datasetName]
  );

  useEffect(() => {
    if (!urlPath) {
      navigate('documentation', { replace: true });
      return;
    }

    const guestAccessiblePaths = isAuthenticated ? ['documentation', 'impact_assessment'] : ['documentation'];
    if (!guestAccessiblePaths.includes(urlPath) && !isLoading && !isV4Loading && !data?.has_access && !V4Data?.has_access) {
      navigate(`/${ver}/${system_model}/dataset/${spold_id}/documentation`, { replace: true });
      return;
    }
  }, [
    navigate,
    spold_id,
    urlPath,
    isLoading,
    data?.has_access,
    ver,
    system_model,
    isV4Loading,
    V4Data?.has_access,
    isAuthenticated,
  ]);

  const fullData: Dataset = data ?? {
    activity_name: 'Dataset',
    geography: {
      short_name: 'GLO',
      long_name: 'Global',
      comment: 'Fallback geography comment',
    },
    reference_product: 'Fallback reference product',
    has_access: true,
    version: '',
    system_model: '',
    index: 0,
    unit: 'kg',
    sector: 'Fallback sector',
  };

  const fullV4Data: v4Dataset = V4Data ?? {
    id: '',
    version: '',
    system_model: '',
    name: 'Dataset',
    geography: 'World',
    geography_short: 'WLD',
    product: 'Fallback functional flow',
    unit: 'kg',
    has_access: false,
  };

  const pageTitle = [fullData.activity_name, fullData.geography.long_name, fullData.reference_product].every(Boolean)
    ? `${fullData.activity_name} - ${fullData.geography.long_name} - ${fullData.reference_product}`
    : 'Dataset';

  const v4PageTitle = [fullV4Data.name, fullV4Data.geography, fullV4Data.product].every(Boolean)
    ? `${fullV4Data.name} - ${fullV4Data.geography} - ${fullV4Data.product}`
    : 'Dataset';

  const geographyIndicator = !isShowingV4 ? fullData.geography.long_name : fullV4Data.geography;
  const timePeriodIndicator = !isShowingV4
    ? documentationData?.activity_description?.time_period
    : documentationV4Data?.time_period;
  const isIndicatorLoading = !isShowingV4 ? isLoading || isDocumentationLoading : isV4Loading || isV4DocumentationLoading;
  // A release serves either the 3.x or the v4 endpoint, never both — the other query is disabled and
  // therefore idle rather than errored, so "not found" is whichever query actually ran for this release.
  const isDatasetNotFound = !isShowingV4 ? isError && !isLoading : isV4Error && !isV4Loading;

  const compareItem: CompareDataset = !isShowingV4
    ? { ...datasetToCompareItem(spold_id ?? '', fullData, { ver, system_model }), timePeriod: timePeriodIndicator }
    : { ...v4DatasetToCompareItem(spold_id ?? '', fullV4Data, { ver, system_model }), timePeriod: timePeriodIndicator };

  const onCompareDataset = () => {
    // addDataset is a no-op when the dataset is already staged or the tray is full, so don't over-report.
    // Matched on the full ref: the same spold id in another release is a different dataset.
    const isAlreadyStaged = comparedDatasets.some((dataset) => isSameDatasetRef(dataset, compareItem));
    const isTrayFull = comparedDatasets.length >= MAX_COMPARE_DATASETS;

    mixpanel.track(MIXPANEL_EVENTS.COMPARE_DATASET_ADDED, {
      dataset_id: compareItem.id,
      dataset_version: compareItem.version,
      dataset_system_model: compareItem.systemModel,
      source: 'dataset_page',
      selection_count: isAlreadyStaged || isTrayFull ? comparedDatasets.length : comparedDatasets.length + 1,
      geography: compareItem.geography,
      tab: urlPath,
    });
    addDataset(compareItem);
    navigate(`/${ver}/${system_model}/search`);
  };

  const isSystemModelUndefined = system_model === 'undefined';

  const unusableUndefinedPaths = ['lci', 'impact_assessment', 'consuming_activities'];
  const currentPath = location.pathname.split('/');

  if (isSystemModelUndefined && currentPath.some((path) => unusableUndefinedPaths.includes(path))) {
    currentPath.splice(currentPath.length - 1, 1, 'documentation');
    const newPath = currentPath.join('/');
    navigate(newPath, { replace: true });
  }

  return (
    <Stack alignItems={'center'} width="100%" spacing={0}>
      <PageHead title={!isShowingV4 ? pageTitle : v4PageTitle} />
      {isDatasetNotFound ? (
        <DatasetNotFound />
      ) : (
        <Stack maxWidth={'container.xl'} width={{ base: '100%', md: '95%' }}>
          <Stack spacing={4} py={5}>
            <Stack spacing={4} px={{ base: 4, md: 0 }}>
              <Breadcrumb fontSize="sm">
                <BreadcrumbItem>
                  <Skeleton isLoaded={!isLoading}>
                    <BreadcrumbLink
                      as={RouterLink}
                      to={searchPath}
                      href={searchPath}
                      onClick={() => {
                        mixpanel.track(MIXPANEL_EVENTS.BREADCRUMB_HOME_CLICKED, {
                          datasetId: spold_id,
                        });
                      }}
                      isTruncated
                    >
                      Datasets
                    </BreadcrumbLink>
                  </Skeleton>
                </BreadcrumbItem>

                <BreadcrumbItem isTruncated fontWeight="medium" isCurrentPage>
                  <Skeleton isTruncated isLoaded={!isLoading}>
                    <BreadcrumbLink isTruncated>{!isShowingV4 ? fullData.activity_name : fullV4Data.name}</BreadcrumbLink>
                  </Skeleton>
                </BreadcrumbItem>
              </Breadcrumb>
              <HStack justifyContent={'space-between'} alignItems={'flex-start'} spacing={4}>
                <Stack spacing={1}>
                  <Skeleton isLoaded={!isLoading} alignSelf={'flex-start'}>
                    <Heading as="h4">{!isShowingV4 ? fullData.activity_name : fullV4Data.name}</Heading>
                  </Skeleton>
                  {(geographyIndicator || timePeriodIndicator) && (
                    <Skeleton isLoaded={!isIndicatorLoading} alignSelf={'flex-start'}>
                      <HStack spacing={6} flexWrap="wrap">
                        {geographyIndicator && (
                          <HStack spacing={1} py={1} minW={0} color={indicatorColor}>
                            <Icon as={IoEarth} flexShrink={0} />
                            <Text isTruncated fontSize="sm">
                              {geographyIndicator}
                            </Text>
                          </HStack>
                        )}
                        {timePeriodIndicator && (
                          <HStack spacing={1} py={1} color={indicatorColor} minW={0}>
                            <Icon as={BiCalendar} flexShrink={0} />
                            <Text isTruncated fontSize="sm">
                              {timePeriodIndicator}
                            </Text>
                          </HStack>
                        )}
                      </HStack>
                    </Skeleton>
                  )}
                </Stack>
                <HStack spacing={2} alignItems="flex-start" flexShrink={0}>
                  {isCompareEnabled && (
                    <Button variant="outline" colorScheme="gray" flexShrink={0} onClick={onCompareDataset}>
                      Compare Dataset
                    </Button>
                  )}
                  {isShowingV4 && (
                    <HStack
                      bg={switchBg}
                      border="1px solid"
                      borderColor={switchBg}
                      borderRadius="8px"
                      p="2px"
                      spacing="1px"
                      flexShrink={0}
                    >
                      <Button
                        size="sm"
                        variant="ghost"
                        borderRadius="6px"
                        fontSize="md"
                        fontWeight="medium"
                        color={switchTextColor}
                        bg={viewMode === 'table' ? switchActiveBg : 'transparent'}
                        _hover={{ bg: viewMode === 'table' ? switchActiveBg : 'blackAlpha.100' }}
                        onClick={() => {
                          setViewMode('table');
                          saveViewMode('table');
                        }}
                      >
                        Table
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        borderRadius="6px"
                        fontSize="md"
                        fontWeight="medium"
                        color={switchTextColor}
                        bg={viewMode === 'graph' ? switchActiveBg : 'transparent'}
                        _hover={{ bg: viewMode === 'graph' ? switchActiveBg : 'blackAlpha.100' }}
                        onClick={() => {
                          if (urlPath !== lastDerivedTablePathRef.current) {
                            setSupplyChainMode(deriveSupplyChainMode(urlPath));
                            lastDerivedTablePathRef.current = urlPath;
                          }
                          setViewMode('graph');
                          saveViewMode('graph');
                        }}
                      >
                        Supply chain
                      </Button>
                    </HStack>
                  )}
                </HStack>
              </HStack>
              {effectiveViewMode === 'table' ? (
                <DatasetTabs isLoading={isLoading} hasAccess={fullData.has_access} />
              ) : (
                <Stack w="100%" overflowX="auto">
                  <Skeleton isLoaded={!isLoading} alignSelf="flex-start" overflow="auto">
                    <Tabs
                      overflowY="hidden"
                      alignSelf="flex-start"
                      size="sm"
                      index={supplyChainMode === 'flows' ? 0 : 1}
                      onChange={(i) => setSupplyChainMode(i === 0 ? 'flows' : 'impact_assessment')}
                    >
                      <TabList maxW="100%" w="max-content">
                        <Tab fontWeight={supplyChainMode === 'flows' ? 'semibold' : 'normal'}>Flows</Tab>
                        <Tab fontWeight={supplyChainMode === 'impact_assessment' ? 'semibold' : 'normal'}>
                          Impact assessment
                        </Tab>
                      </TabList>
                    </Tabs>
                  </Skeleton>
                </Stack>
              )}
              {isSystemModelUndefined && (
                <Alert status="info" variant="left-accent" borderRadius="md">
                  <AlertIcon />
                  <AlertDescription>
                    You're currently viewing an 'undefined' (unlinked, multi-output) dataset.{' '}
                    <Link
                      href={'https://support.ecoinvent.org/upr-lci-lcia'}
                      target="_blank"
                      title="Learn more about undefined datasets"
                      fontWeight={'semibold'}
                      colorScheme="blue"
                    >
                      Learn more about the difference between linked and undefined datasets.
                    </Link>
                  </AlertDescription>
                </Alert>
              )}
            </Stack>
            {fullData.has_access || fullV4Data.has_access ? (
              <PrivateDatasetView
                path={urlPath}
                isLoading={isLoading}
                viewMode={effectiveViewMode}
                supplyChainMode={supplyChainMode}
              />
            ) : urlPath === 'impact_assessment' && !isShowingV4 && isAuthenticated ? (
              <Stack spacing={5}>
                <UpgradeNowUpsellDataset />
                <ImpactAssessment hideScores />
              </Stack>
            ) : (
              <PublicDatasetView datasetId={spold_id ?? ''} isLoading={isLoading} />
            )}
            {hasALicence && !isShowingV4 && <RelatedDatasets url_path={urlPath} id={spold_id as string} />}
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};
export default DatasetView;
